import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import { AppRoot } from './app/AppRoot';
import { bootstrap } from './app/bootstrap';
import { DatabaseInUse, type SqlDriver } from './data/db/driver';
import { openDatabase } from './data/db/openDatabase';
import { handOverWhenAsked, OtherTabScreen, rememberLanguage } from './app/otherTab';
import { resources } from './i18n/i18n';
import '@fontsource-variable/hanken-grotesk';
import './ui/styles';

const root = createRoot(document.getElementById('root')!);

async function followSystemTextSize() {
  try {
    const [{ TextZoom }, { App }] = await Promise.all([import('@capacitor/text-zoom'), import('@capacitor/app')]);
    const apply = async () => {
      const { value } = await TextZoom.getPreferred();
      await TextZoom.set({ value });
    };
    await apply();
    await App.addListener('resume', () => void apply());
  } catch (e) {
    console.warn('[text-zoom]', e);
  }
}

function start(open: () => Promise<SqlDriver>) {
  bootstrap(open)
    .then((services) => {
      // Read by the Android/iOS smoke checks (logcat / simulator log) to prove the DB opened.
      console.info(`[bootstrap] ready platform=${services.platform} storage=${services.db.kind} schema=${services.schemaVersion} at=${Math.round(performance.now())}ms`);
      // Device checks (e2e, emulator) drive the store directly. Opt-in per device, never on by default.
      try {
        if (localStorage.getItem('budget.debug') === '1') (window as unknown as { __budget: unknown }).__budget = services;
      } catch {
        /* storage unavailable */
      }
      // The web build works offline once visited: a service worker keeps the app's files (never on iOS/Android, which ship them).
      if (services.platform === 'web' && import.meta.env.PROD && 'serviceWorker' in navigator) {
        void navigator.serviceWorker.register('/sw.js').catch((e: unknown) => console.warn('[offline]', e));
      }
      // Phones: text follows the system's text size (Android font scale, iOS Dynamic Type), now and after each return to the app.
      if (services.platform !== 'web') void followSystemTextSize();
      root.render(
        <StrictMode>
          <BrowserRouter>
            <AppRoot services={services} />
          </BrowserRouter>
        </StrictMode>,
      );
      if (services.platform === 'web') {
        // Another tab may ask for the database ("Use it here"): let go of it and say where it went.
        handOverWhenAsked(() => services.db.release?.() ?? Promise.resolve(), () => root.render(<OtherTabScreen moved />));
        rememberLanguage(services.i18n.language);
        services.i18n.on('languageChanged', rememberLanguage);
      }
    })
    .catch((error: unknown) => {
      // Open in another tab: say so, and start as soon as the database is ours.
      if (error instanceof DatabaseInUse) {
        root.render(<OtherTabScreen />);
        void error.whenFree().then((db) => start(async () => db), fail);
        return;
      }
      fail(error);
    });
}

function fail(error: unknown) {
  // i18n may not exist yet; pick the fatal copy directly.
  console.error('[bootstrap]', error);
  const copy = navigator.language.toLowerCase().startsWith('tr') ? resources.tr : resources.en;
  root.render(
    <main className="fatal" role="alert">
      <h1>{copy.translation.fatal.title}</h1>
      <p>{copy.translation.fatal.body}</p>
    </main>,
  );
}

start(openDatabase);
